/** @format */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbDate, NgbModal, NgbModalOptions, NgbModalRef, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Profile } from 'src/app/models/profile';
import { UserService } from 'src/app/services';
import { getPayPeriods, getProfile, getUserDelegationEntries } from 'src/app/store';
import {
	AddDelegation,
	ApplicationState,
	DelegationEntry,
	DeleteDelegation,
	FormSortableColumns,
	FormSortDirection,
	GetTimekeeperRelatedDelegations,
	GetUserDelegations,
	NotificationStates,
	PayPeriod,
	ToastUserNotification,
	UpdateDelegation,
	UserNotification,
	UsersList,
} from 'src/app/store/application';
import * as HelperFunctions from '../../../assets/helper-functions/helper-functions';

@Component({
	selector: 'app-delegations',
	templateUrl: './delegations.component.html',
	styleUrls: ['./delegations.component.scss'],
})
export class DelegationsComponent implements OnInit, OnDestroy {
	delegations$: Observable<DelegationEntry[]>;
	delegations: DelegationEntry[] = [];

	profile$: Observable<Profile>;
	profile: Profile | null = null;

	payPeriods$: Observable<PayPeriod[]>;
	payPeriods!: PayPeriod[];

	usersList: UsersList[] = [];
	selectableUsersListFrom: UsersList[] = [];
	selectableUsersListTo: UsersList[] = [];

	isManager: boolean = false;
	isTimekeeper: boolean = false;

	@ViewChild('deleteModal') deleteModal: any;
	delegationForm: FormGroup;
	isEdit: boolean = false;
	isDelete: boolean = false;

	deleteModalRef!: NgbModalRef;
	delegationIdToDelete: number = -1;

	readonly ngbModalOptions: NgbModalOptions = {
		backdrop: 'static',
		keyboard: false,
		centered: true,
	};

	private ngUnsubscribe: Subject<void> = new Subject();

	allFormsValid = false;
	minDateFrom = new NgbDate(2022, 1, 1);
	minDateTo = new NgbDate(2022, 1, 1);

	formStateValidity: any = {
		toUser: false,
		from: false,
		to: false,
	};

	formTouchedState: any = {
		toUser: false,
		from: false,
		to: false,
	};

	showFromControl: boolean = false;

	roleList: any[] = [
		{ roleName: 'Manager', id: 0 },
		{ roleName: 'Timekeeper', id: 1 },
	];

	formSortState: FormSortableColumns = {
		roleSort: false,
		fromUserSort: false,
		toUserSort: false,
		startDateSort: true,
		endDateSort: false,
		statusSort: false,
	};

	formSortDirection: FormSortDirection = {
		ascending: false,
		descending: true,
	};

	constructor(
		private store: Store<ApplicationState>,
		private userService: UserService,
		private modalService: NgbModal,
		private offcanvasService: NgbOffcanvas
	) {
		this.delegations$ = this.store.select(getUserDelegationEntries);
		this.profile$ = this.store.select(getProfile);

		this.delegationForm = new FormGroup({
			id: new FormControl(-1),
			toUser: new FormControl(null, Validators.required),
			from: new FormControl(null, Validators.required),
			to: new FormControl(null, Validators.required),
			active: new FormControl(true),
		});

		document.addEventListener('click', this.handleListeners.bind(this));
		document.addEventListener('keydown', this.handleListeners.bind(this));
		this.payPeriods$ = this.store.select(getPayPeriods);
	}

	ngOnInit(): void {
		this.delegations$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((delegations) => {
			this.delegations = [...delegations];
			this.setFormSortedColumn('', true);
		});

		this.profile$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((profile) => {
			this.profile = profile;

			if (profile.user.isTimekeeper) {
				this.formStateValidity.role = false;
				this.formTouchedState.role = false;
				this.delegationForm.addControl('role', new FormControl(null, Validators.required));
			}

			// if the user is a manager, then show a list of users who are managers to delegate to
			// if the user is a timekeeper, then show a list of users who are timekeepers to delegate to
			this.isManager = profile.user.isManager;
			this.isTimekeeper = profile.user.isTimekeeper;

			this.userService
				.getUsers()
				.pipe(takeUntil(this.ngUnsubscribe))
				.subscribe((users) => {
					users.forEach(
						(user) =>
							((user.isManager && this.isManager) || ((user.isTimekeeper || user.isManager) && this.isTimekeeper)) &&
							this.usersList.push({
								id: user.id!,
								fullName: `${user.firstName} ${user.lastName}`,
								isManager: user.isManager,
								isTimekeeper: user.isTimekeeper,
							})
					);

					this.usersList.sort((a: UsersList, b: UsersList) => {
						if (a.fullName < b.fullName) return -1;
						if (a.fullName > b.fullName) return 1;
						return 0;
					});

					// the timekeeper should select what role they are targetting first
					this.selectableUsersListTo = this.isTimekeeper ? [] : this.usersList.filter((user) => user.id !== this.profile?.user.id);

					if (this.isTimekeeper) this.store.dispatch(GetTimekeeperRelatedDelegations());
					else this.store.dispatch(GetUserDelegations());
				});

			this.payPeriods$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((payPeriods) => (this.payPeriods = payPeriods));
		});
	}

	ngOnDestroy(): void {
		document.removeAllListeners!('click');
		document.removeAllListeners!('keydown');
	}

	getIsColumnSorted(columnSortType: string) {
		let columnKey = columnSortType as keyof FormSortableColumns;
		return this.formSortState[columnKey];
	}

	getSortIcon(columnSortType: string) {
		let columnKey = columnSortType as keyof FormSortableColumns;
		// if (!this.formSortState[columnKey]) return `<i class="fa-solid fa-bars-sort"></i>`;
		if (!this.formSortState[columnKey]) return `<i class="fa-solid fa-sort"></i>`;
		if (this.formSortDirection.ascending) return `<i class="fa-solid fa-sort-up"></i>`;
		return '<i class="fa-solid fa-sort-down"></i>';
	}

	getIsAscending = () => this.formSortDirection.ascending;

	setFormSortedColumn(columnSortType: string = '', isDataReloadSort: boolean = false) {
		if (!columnSortType.length) {
			if (this.formSortState.roleSort) columnSortType = 'roleSort';
			else if (this.formSortState.fromUserSort) columnSortType = 'fromUserSort';
			else if (this.formSortState.toUserSort) columnSortType = 'toUserSort';
			else if (this.formSortState.startDateSort) columnSortType = 'startDateSort';
			else if (this.formSortState.endDateSort) columnSortType = 'endDateSort';
			else columnSortType = 'statusSort';
		}

		let columnKey = columnSortType as keyof FormSortableColumns;

		// if we are reversing the sort by clicking on the currently sorted column, then flip the sort
		if (this.formSortState[columnKey]) {
			// we dont want swap the sort order if it is caused by a new entry. Only when it is clicked do we resort
			if (!isDataReloadSort) {
				this.formSortDirection.ascending = !this.formSortDirection.ascending;
				this.formSortDirection.descending = !this.formSortDirection.descending;
			}
		} else {
			this.formSortState = {
				roleSort: false,
				fromUserSort: false,
				toUserSort: false,
				startDateSort: false,
				endDateSort: false,
				statusSort: false,
			};

			this.formSortState[columnKey] = true;
			this.formSortDirection.descending = true;
			this.formSortDirection.ascending = false;
		}

		this.resortFormData();
	}

	resortFormData() {
		if (this.formSortState.roleSort) {
			// for role sort, sort by the fromStartDate field and then sort by role
			let sortedDelegations = [...this.delegations].sort((a: any, b: any) => b.from.getTime() - a.from.getTime());
			let timeKeeperDelegations = sortedDelegations.filter((d) => d.role === 'Timekeeper');
			let managerDelegations = sortedDelegations.filter((d) => d.role === 'Manager');

			this.delegations = this.formSortDirection.ascending
				? [...managerDelegations, ...timeKeeperDelegations]
				: [...timeKeeperDelegations, ...managerDelegations];
		} else {
			let sortedDelegations: DelegationEntry[] = [];

			if (this.formSortState.fromUserSort) {
				sortedDelegations = [...this.delegations]
					.sort((a: DelegationEntry, b: DelegationEntry) => b.from.getTime() - a.from.getTime())
					.sort((a: DelegationEntry, b: DelegationEntry) => {
						let aFullName = this.getUserFromUserId(a.fromUserId);
						let bFullName = this.getUserFromUserId(b.fromUserId);

						if (aFullName > bFullName) return this.formSortDirection.ascending ? 1 : -1;
						if (aFullName < bFullName) return this.formSortDirection.ascending ? -1 : 1;
						return 0;
					});
			}

			if (this.formSortState.toUserSort) {
				sortedDelegations = [...this.delegations]
					.sort((a: DelegationEntry, b: DelegationEntry) => b.from.getTime() - a.from.getTime())
					.sort((a: DelegationEntry, b: DelegationEntry) => {
						let aFullName = this.getUserFromUserId(a.toUserId);
						let bFullName = this.getUserFromUserId(b.toUserId);

						if (aFullName > bFullName) return this.formSortDirection.ascending ? 1 : -1;
						if (aFullName < bFullName) return this.formSortDirection.ascending ? -1 : 1;
						return 0;
					});
			}

			if (this.formSortState.startDateSort) {
				sortedDelegations = [...this.delegations].sort((a: DelegationEntry, b: DelegationEntry) => {
					return this.formSortDirection.ascending ? a.from.getTime() - b.from.getTime() : b.from.getTime() - a.from.getTime();
				});
			}

			if (this.formSortState.endDateSort) {
				sortedDelegations = [...this.delegations].sort((a: DelegationEntry, b: DelegationEntry) => {
					return this.formSortDirection.ascending ? a.to.getTime() - b.to.getTime() : b.to.getTime() - a.to.getTime();
				});
			}

			if (this.formSortState.statusSort) {
				sortedDelegations = [...this.delegations]
					.sort((a: DelegationEntry, b: DelegationEntry) => b.from.getTime() - a.from.getTime())
					.sort((a: DelegationEntry, b: DelegationEntry) => {
						return a.active === b.active
							? 0
							: a.active
							? this.formSortDirection.ascending
								? -1
								: 1
							: this.formSortDirection.ascending
							? 1
							: -1;
					});
			}

			this.delegations = sortedDelegations;

			// let timeKeeperDelegations = sortedDelegations.filter((d) => d.role === 'Timekeeper');
			// let managerDelegations = sortedDelegations.filter((d) => d.role === 'Manager');
			// this.delegations = [...timeKeeperDelegations, ...managerDelegations];
		}
	}

	handleListeners(event: any) {
		if (
			event.target?.className === 'd-block modal fade show modal-static' ||
			event.target?.className === 'offcanvas-backdrop fade' ||
			event.key === 'Escape'
		) {
			if (this.modalService.hasOpenModals()) {
				this.closeDeleteModal(false);
			}

			if (this.offcanvasService.hasOpenOffcanvas() || !this.modalService.hasOpenModals()) {
				this.closeSidePanel();
			}
		}
	}

	getUserFromUserId(userId: number) {
		return this.usersList.find((user) => user.id === userId)?.fullName || '';
	}

	getDateString(date: Date) {
		return HelperFunctions.getDateString(date);
	}

	// handleChange(formControl: string) {
	//   this.doErrorChecking(formControl);

	//   if (formControl === 'fromUser') {
	//     this.delegationForm.controls.toUser.setValue(null);
	//     this.filterRemainingUsers(this.delegationForm.value.fromUser);
	//   }
	// }

	// filterRemainingUsers(userIdToExclude: number) {
	//   this.filteredUsersList = this.usersList.filter(user => user.id !== userIdToExclude);
	// }

	onDateSelect(date: NgbDate, formControl: string) {
		let selectedDate = new Date(date.year, date.month - 1, date.day);
		this.delegationForm.controls[formControl].setValue(this.getDateString(selectedDate));
		this.doErrorChecking(formControl);

		if (formControl === 'from') {
			this.minDateTo = new NgbDate(date.year, date.month, date.day);
			let toDateStr = this.delegationForm.controls['to'].value;

			if (toDateStr !== null) {
				let toDate = new Date(toDateStr);
				let differenceInDays = HelperFunctions.getDifferenceInDays(selectedDate, toDate);

				if (differenceInDays < 0) {
					this.delegationForm.controls['to'].reset();

					this.formStateValidity.to = false;
					this.allFormsValid = false;
				}
			}
		}
	}

	isSelectedDate(date: NgbDate, formControl: string) {
		let selectedDateValue = this.delegationForm.controls[formControl].value;

		if (selectedDateValue !== null) {
			let selectedDate = new Date(selectedDateValue);
			return selectedDate.getDate() === date.day && selectedDate.getMonth() + 1 === date.month && selectedDate.getFullYear() === date.year;
		}

		return false;
	}

	doErrorChecking(formControl: string, userTouchedOverride: boolean = false) {
		this.formTouchedState[formControl] = this.delegationForm.controls[formControl].touched || userTouchedOverride;
		this.formStateValidity[formControl] = this.delegationForm.controls[formControl].valid;
		this.allFormsValid = Array.from(Object.values(this.formStateValidity)).every((state) => state === true);

		switch (formControl) {
			case 'role':
				this.setShowFromControl();
				break;

			case 'fromUser':
				this.setFilteredToUsers();
				break;

			case 'toUser':
				this.setFilteredFromUsers();
				break;

			default:
				break;
		}
	}

	setShowFromControl() {
		let selectedRoleId = this.delegationForm.controls['role'].value;

		if (selectedRoleId === null) {
			this.selectableUsersListFrom = [];
			this.selectableUsersListTo = [];
			this.showFromControl = false;
			delete this.formStateValidity.fromUser;
			delete this.formTouchedState.fromUser;
			this.delegationForm.removeControl('fromUser');
		} else if (selectedRoleId === 0) {
			// we are selecting a manager role for the delegation
			this.showFromControl = true;
			this.formStateValidity.fromUser = false;
			this.formTouchedState.fromUser = false;
			this.delegationForm.addControl('fromUser', new FormControl(null, Validators.required));

			// cannot assign a delegation from someone else to yourself
			this.selectableUsersListFrom = this.usersList.filter((u) => u.isManager);
			this.selectableUsersListTo = this.usersList.filter((u) => u.isManager);
		} else {
			// we are selecting a timekeeper role for the delegation
			this.showFromControl = false;
			delete this.formStateValidity.fromUser;
			delete this.formTouchedState.fromUser;
			this.delegationForm.removeControl('fromUser');

			// cannot assign yourself to be your delegation
			this.selectableUsersListTo = this.usersList.filter((u) => u.isTimekeeper && u.id !== this.profile?.user.id);
		}
	}

	setFilteredFromUsers() {
		if (this.showFromControl) {
			let toUserId = this.delegationForm.controls['toUser'].value;
			this.selectableUsersListFrom = this.usersList.filter((u) => u.isManager && u.id !== this.profile?.user.id && u.id !== toUserId);
		}
	}

	setFilteredToUsers() {
		let fromUserId = this.delegationForm.controls['fromUser'].value;
		this.selectableUsersListTo = this.usersList.filter((u) => u.isManager && u.id !== fromUserId);
	}

	getIsUserAssignee(delegation: DelegationEntry) {
		return delegation.toUserId === this.profile?.user.id;
	}

	getIsDelegationCreator(delegation: DelegationEntry) {
		return delegation.createdBy === this.profile?.user.id;
	}

	getRoleListEntry = (delegation: DelegationEntry) => this.roleList.find((r) => r.roleName === delegation.role);

	getDelegationRole(delegation: DelegationEntry) {
		return this.getRoleListEntry(delegation).roleName;
	}

	shouldDarkenEntryBG(date: NgbDate) {
		const payPeriod = this.payPeriods.findIndex((d) => this.isInPP(date, new Date(d.startDate), new Date(d.endDate)));
		if (payPeriod > -1) {
			return payPeriod % 2 === 0;
		}
		return false;
	}

	isInPP(targetDate: NgbDate, start: Date, end: Date) {
		const ngbStart = new NgbDate(start.getFullYear(), start.getMonth() + 1, start.getDate());
		const ngbEnd = new NgbDate(end.getFullYear(), end.getMonth() + 1, end.getDate());
		if (targetDate.equals(ngbStart) || targetDate.equals(ngbEnd) || (targetDate.after(ngbStart) && targetDate.before(ngbEnd))) {
			return true;
		}
		return false;
	}

	openSidePanel(sidePanel: any) {
		this.closeAnyOffCanvas();
		this.offcanvasService.open(sidePanel, { ariaLabelledBy: 'offcanvas-basic-title', position: 'end' });
	}

	openEditSidePanel(delegation: DelegationEntry, sidePanel: any) {
		// if (delegation.toUserId === this.profile?.user.id) {
		if (this.getIsUserAssignee(delegation) && !this.getIsDelegationCreator(delegation)) {
			// toast to user that you can't edit a delegation assigned to you
			const notification: UserNotification = {
				type: NotificationStates.Error,
				title: 'Delegation Edit Denied',
				message: `Delegations that are assigned to you can only be edited by the assignor.`,
			};

			this.store.dispatch(ToastUserNotification({ notification }));
			return;
		}

		this.delegationForm.controls.id.setValue(delegation.id);
		this.delegationForm.controls.toUser.setValue(delegation.toUserId);
		this.delegationForm.controls.from.setValue(this.getDateString(delegation.from));
		this.delegationForm.controls.to.setValue(this.getDateString(delegation.to));
		this.delegationForm.controls.active.setValue(delegation.active);

		this.formStateValidity = {
			user: true,
			from: true,
			to: true,
			description: true,
		};

		if (this.isTimekeeper) {
			this.delegationForm.controls.role.setValue(this.getRoleListEntry(delegation).id);
			this.formStateValidity.role = true;

			this.setShowFromControl();
			if (this.showFromControl) {
				this.delegationForm.controls.fromUser.setValue(delegation.fromUserId);
				this.formStateValidity.fromUser = true;
			}
		}

		this.allFormsValid = true;
		this.isEdit = true;

		this.closeAnyOffCanvas();
		this.openSidePanel(sidePanel);
	}

	closeSidePanel() {
		this.closeAnyOffCanvas();
		this.handleFormCleanup();
	}

	closeAnyOffCanvas() {
		if (this.offcanvasService.hasOpenOffcanvas()) {
			this.offcanvasService.dismiss();
		}
	}

	closeDeleteModal(shouldFireSubmit: boolean) {
		this.deleteModalRef.close(shouldFireSubmit);
	}

	deleteDelegation(delegation: DelegationEntry) {
		if (this.getIsUserAssignee(delegation) && !this.getIsDelegationCreator(delegation)) {
			// toast to user that you can't edit a delegation assigned to you
			const notification: UserNotification = {
				type: NotificationStates.Error,
				title: 'Delegation Delete Denied',
				message: `Delegations that are assigned to you can only be deleted by the assignor.`,
			};

			this.store.dispatch(ToastUserNotification({ notification }));
			return;
		}

		this.delegationIdToDelete = delegation.id!;
		this.deleteModalRef = this.modalService.open(this.deleteModal, this.ngbModalOptions);

		this.deleteModalRef.result.then((shouldDelete: boolean) => {
			if (shouldDelete) this.store.dispatch(DeleteDelegation({ id: this.delegationIdToDelete }));
			// this.delegationIdToDelete = -1;
		});
	}

	submitDelegationForm() {
		let result = this.delegationForm.value;
		let delegation: DelegationEntry = {
			id: result.id || -1,
			fromUserId: result.fromUser ? result.fromUser : this.profile?.user.id!,
			toUserId: result.toUser,
			from: HelperFunctions.getBeginningOfDayDate(new Date(result.from)),
			to: HelperFunctions.getBeginningOfDayDate(new Date(result.to)),
			role: result.role !== undefined ? this.roleList.find((r) => r.id === result.role).roleName : 'Manager',
		};

		if (this.isEdit) {
			this.store.dispatch(UpdateDelegation({ delegation }));
		} else {
			this.store.dispatch(AddDelegation({ delegation, isTimekeeper: this.isTimekeeper }));
		}

		this.closeSidePanel();
	}

	handleFormCleanup() {
		setTimeout(() => {
			this.delegationForm.reset();
			if (this.isTimekeeper) this.setShowFromControl();
		}, 500);

		this.isEdit = false;
		this.allFormsValid = false;
		this.minDateTo = new NgbDate(2022, 1, 1);

		this.formStateValidity = {
			toUser: false,
			from: false,
			to: false,
		};

		this.formTouchedState = {
			toUser: false,
			from: false,
			to: false,
		};

		if (this.isTimekeeper) {
			this.formStateValidity.role = false;
			this.formTouchedState.role = false;

			this.selectableUsersListFrom = [];
			this.selectableUsersListTo = [];
		}
	}
}
